// Ringtone system — supports preset tones (Web Audio API) + custom audio files
let audioContext = null;
let ringtoneInterval = null;
let isPlaying = false;
let customAudioElement = null;

// Current selected ringtone (defaults to 'classic')
let currentRingtone = 'classic';

function getAudioContext() {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
}

// ==================== PRESET RINGTONE GENERATORS ====================

function playClassicRing(ctx) {
  const now = ctx.currentTime;
  const duration = 0.8;
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.15, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  const osc1 = ctx.createOscillator();
  osc1.type = 'sine';
  osc1.frequency.value = 440;
  osc1.connect(gain);
  osc1.start(now);
  osc1.stop(now + duration);
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.value = 480;
  osc2.connect(gain);
  osc2.start(now);
  osc2.stop(now + duration);
}

function playModernBeep(ctx) {
  const now = ctx.currentTime;
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  // Three quick ascending beeps
  [800, 1000, 1200].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain);
    osc.start(now + i * 0.12);
    osc.stop(now + i * 0.12 + 0.1);
  });
}

function playAlertTone(ctx) {
  const now = ctx.currentTime;
  const duration = 0.6;
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.18, now);
  gain.gain.linearRampToValueAtTime(0.18, now + 0.3);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(600, now);
  osc.frequency.linearRampToValueAtTime(900, now + 0.3);
  osc.frequency.linearRampToValueAtTime(600, now + duration);
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + duration);
}

function playDigitalChirp(ctx) {
  const now = ctx.currentTime;
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.1, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  // Descending chirp
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(1400, now);
  osc.frequency.exponentialRampToValueAtTime(400, now + 0.5);
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + 0.5);
}

function playSoftChime(ctx) {
  const now = ctx.currentTime;
  // Gentle two-note chime
  [523, 659].forEach((freq, i) => {
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    const start = now + i * 0.25;
    gain.gain.setValueAtTime(0.08, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.8);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain);
    osc.start(start);
    osc.stop(start + 0.8);
  });
}

function playUrgentAlarm(ctx) {
  const now = ctx.currentTime;
  // Fast alternating two-tone alarm
  for (let i = 0; i < 4; i++) {
    const t = now + i * 0.2;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.14, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = i % 2 === 0 ? 800 : 600;
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + 0.18);
  }
}

// ==================== PRESET RINGTONES CONFIG ====================

export const PRESET_RINGTONES = [
  { id: 'classic', name: 'Classic Ring', description: 'Traditional dual-tone phone ring' },
  { id: 'modern_beep', name: 'Modern Beep', description: 'Three ascending quick beeps' },
  { id: 'alert_tone', name: 'Alert Tone', description: 'Rising alarm-style square wave' },
  { id: 'digital_chirp', name: 'Digital Chirp', description: 'Descending digital sweep' },
  { id: 'soft_chime', name: 'Soft Chime', description: 'Gentle two-note chime' },
  { id: 'urgent_alarm', name: 'Urgent Alarm', description: 'Fast alternating alarm tones' },
];

const PRESET_PLAY_MAP = {
  classic: playClassicRing,
  modern_beep: playModernBeep,
  alert_tone: playAlertTone,
  digital_chirp: playDigitalChirp,
  soft_chime: playSoftChime,
  urgent_alarm: playUrgentAlarm,
};

const PRESET_INTERVALS = {
  classic: 2500,
  modern_beep: 2000,
  alert_tone: 2500,
  digital_chirp: 2000,
  soft_chime: 3000,
  urgent_alarm: 1800,
};

// ==================== RINGTONE CONTROL API ====================

export function setRingtone(ringtoneId) {
  currentRingtone = ringtoneId;
  try {
    localStorage.setItem('pbx_ringtone', ringtoneId);
  } catch { /* ignore */ }
}

export function getRingtone() {
  try {
    return localStorage.getItem('pbx_ringtone') || 'classic';
  } catch {
    return 'classic';
  }
}

export function setCustomAudioUrl(url) {
  try {
    localStorage.setItem('pbx_custom_ringtone_url', url);
  } catch { /* ignore */ }
}

export function getCustomAudioUrl() {
  try {
    return localStorage.getItem('pbx_custom_ringtone_url') || '';
  } catch {
    return '';
  }
}

// Preview a preset tone (plays once)
export function previewRingtone(ringtoneId) {
  stopRingtone();
  if (ringtoneId === 'custom') {
    const url = getCustomAudioUrl();
    if (url) {
      const audio = new Audio(url);
      audio.volume = 0.5;
      audio.play().catch(() => {});
      setTimeout(() => { audio.pause(); audio.currentTime = 0; }, 3000);
    }
    return;
  }
  const playFn = PRESET_PLAY_MAP[ringtoneId];
  if (playFn) {
    try {
      const ctx = getAudioContext();
      playFn(ctx);
    } catch { /* ignore */ }
  }
}

export function startRingtone() {
  if (isPlaying) return;
  isPlaying = true;

  const ringtoneId = getRingtone();

  // Custom audio file
  if (ringtoneId === 'custom') {
    const url = getCustomAudioUrl();
    if (url) {
      try {
        customAudioElement = new Audio(url);
        customAudioElement.loop = true;
        customAudioElement.volume = 0.6;
        customAudioElement.play().catch(() => {});
      } catch { /* ignore */ }
    }
    return;
  }

  // Preset tones
  const playFn = PRESET_PLAY_MAP[ringtoneId] || playClassicRing;
  const interval = PRESET_INTERVALS[ringtoneId] || 2500;

  try {
    const ctx = getAudioContext();
    playFn(ctx);
    ringtoneInterval = setInterval(() => {
      if (isPlaying) playFn(ctx);
    }, interval);
  } catch (e) {
    console.warn('Ringtone failed:', e);
  }
}

export function stopRingtone() {
  isPlaying = false;
  if (ringtoneInterval) {
    clearInterval(ringtoneInterval);
    ringtoneInterval = null;
  }
  if (customAudioElement) {
    customAudioElement.pause();
    customAudioElement.currentTime = 0;
    customAudioElement = null;
  }
}

// Initialize from localStorage on load
currentRingtone = getRingtone();
